import { getTasks, type Task } from '../../packages/api-client/pm-api';

/**
 * A planning request's failure as a sentence. Planning routes answer
 * {"code","message"}; the AI route answers {"error"}. 403 is a permission
 * decision, not a fault, and a dependency cycle is the one scheduling input
 * error with a known fix.
 */
export function planErrorMessage(err: unknown, action: string): string {
    const res = (err as { response?: { status?: number; data?: { message?: string; error?: string } } })?.response;
    const msg = res?.data?.message ?? res?.data?.error ?? '';
    if (res?.status === 403) return `You don't have permission to ${action}.`;
    if (res?.status === 400 && msg.toLowerCase().includes('cycle')) return 'The dependencies form a cycle. Remove one of the dependencies in the cycle and try again.';
    if (!res) return `Couldn't reach the server to ${action}. Check your connection and try again.`;
    return msg ? `Couldn't ${action}: ${msg}` : `Couldn't ${action} (error ${res.status ?? 'unknown'}).`;
}

/** Most tasks the plan and timeline load (100 pages of 100). */
export const TASK_CAP = 10000;

/**
 * Every task in the project up to TASK_CAP. The list endpoint pages (default
 * 20, max 100), and the plan, schedule preview and Gantt must cover the whole
 * project, so page until meta.total. `truncated` says the cap cut it short.
 */
export async function getAllTasks(projectId: string): Promise<{ tasks: Task[]; truncated: boolean }> {
    const out: Task[] = [];
    let total = 0;
    for (let page = 1; out.length < TASK_CAP; page++) {
        const res = await getTasks(projectId, { flat: 'true', limit: '100', page: String(page) });
        const batch = res.data ?? [];
        out.push(...batch);
        total = res.meta?.total ?? 0;
        if (batch.length < 100 || out.length >= total) return { tasks: out, truncated: false };
    }
    return { tasks: out.slice(0, TASK_CAP), truncated: total > TASK_CAP };
}

/** Runs fn over items with at most `limit` calls in flight; results keep input order. */
export async function mapLimit<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
    const out = new Array<R>(items.length);
    let next = 0;
    const worker = async () => {
        while (next < items.length) {
            const i = next++;
            out[i] = await fn(items[i]);
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return out;
}
